import {FilterActions, FilterLabels} from '@aboutyou/legacy-tracking-sdk';
import {
  LocationType,
  SRT_PARAM,
  UTM_PARAMS,
  useHistory,
  useQueryParams,
  useQueryParamsCallback,
} from '@aboutyou/router';
import {useTrackFilter} from '@aboutyou/tracking';
import {useCallback, useRef} from 'react';
import {INVALIDATE_CAMPAIGN_PARAM} from '../../../backend-services/Campaigns/DiscountsRefresh/constants';
import {unisexOnlineMarketingParam} from '../../../backend-services/CategoryStream/utils/omEntry';
import {SEARCH_TERM_PARAM} from '../../../backend-services/Search/hooks/useSearchTerm';
import {WALLET_STATE_MANAGER_PARAM} from '../../../backend-services/Wallet/utils/useWalletStateManager';
import {
  HIDE_FOOTER_QUERY_PARAM,
  HIDE_HEADER_QUERY_PARAM,
} from '../../../client-services/LaunchTeaser/useFullScreenView';
import {
  RANGE_FILTER_DELIMITER,
  isRangeFilter,
} from '../../../shared-deprecated/rangeFilterUtils';
import {
  AuthSearchParams,
  SSOParams,
} from '../../Authentication/hooks/constants';
import {CRM_COUPON_TOKEN_PARAM} from '../../Coupons/utils/crmCouponTokenParam';
import {FILTER_KEY} from '../../Filters/shared/useFilterFlyout';
import {
  DISABLE_HEADER_HIGHLIGHT_PARAM,
  HEADER_HIGHLIGHT_OVERRIDE_PARAM,
} from '../../PromotionBanner/utils/headerHighlightParams';
import {composeAttributeFilterId} from './composeAttributeFilter';
import {getUpdatedFilterParams} from './getUpdatedFilterParams';
import {
  isBooleanFilter,
  parseAttributeFilterValue,
  parseQueryParamsFilterValues,
} from './parseQueryParamsFilterValues';
import {LIVE_AT_FILTER_KEY} from './useAppliedLiveAtFilter';

/**
 * Marketing params critical for campaigns, that should not be removed
 * https://aboutyou.atlassian.net/browse/ASFE-12784
 */
const MARKETING_PARAMS_CAMPAIGN = ['cjevent', 'cjdata', 'fbclid', 'gclid'];

const IGNORE_FILTER_KEYS = [
  ...MARKETING_PARAMS_CAMPAIGN,
  ...UTM_PARAMS,
  FILTER_KEY,
  LIVE_AT_FILTER_KEY,
  'sort',
  'category',
  'cluster',
  'enableBotMode',
  'force-global',
  'force-language',
  HIDE_HEADER_QUERY_PARAM,
  HIDE_FOOTER_QUERY_PARAM,
  'ssr',
  'cacheBreaker',
  INVALIDATE_CAMPAIGN_PARAM,
  'disableWebTracking',
  'disableDatadogBrowser',
  'disableSessionRecording',
  'version',
  'disableConsentBanner',
  DISABLE_HEADER_HIGHLIGHT_PARAM,
  HEADER_HIGHLIGHT_OVERRIDE_PARAM,
  'feature-flag-overrides',
  'tadaridaBackendApiOverride',
  unisexOnlineMarketingParam,
  'campaignOverlay',
  CRM_COUPON_TOKEN_PARAM,
  WALLET_STATE_MANAGER_PARAM,
  'mobileSite',
  SRT_PARAM,
  SEARCH_TERM_PARAM,
  ...Object.values(AuthSearchParams),
  ...Object.values(SSOParams),
];

export type FilterObjForBulkTracking = {
  section: string;
  slug: boolean | [number, number] | number[] | string[];
  type: 'boolean' | 'range' | 'attribute';
  action: FilterActions;
  label: FilterLabels;
};

export interface SetFilterObject {
  filterId: string;
  filterSection: string;
  filterSlug?: string;
  singleSelectOnly?: boolean;
  label: FilterLabels;
  shouldTrackImmediately?: boolean;
  trackingParams?: {
    attributeGroup: string;
  };
}

export type SetFiltersWithoutToggleType = Array<{
  filterSection: string;
  filterValues: string[] | undefined;
}>;

const getFiltersFromQueryParams = (
  queryParams: URLSearchParams,
): Map<string, Array<string> | undefined> => {
  const activeFilterSectionsKeys = Array.from(queryParams.keys()).filter(
    key => !IGNORE_FILTER_KEYS.includes(key),
  );

  return activeFilterSectionsKeys.reduce((filters, filterSectionKey) => {
    return filters.set(
      filterSectionKey,
      queryParams
        .get(filterSectionKey)
        ?.split(isRangeFilter(filterSectionKey) ? RANGE_FILTER_DELIMITER : ',')
        .map(parseQueryParamsFilterValues),
    );
  }, new Map());
};

/**
 * Returns all query params that are not ignored explicitly as potential filters.
 * E.g. ?color=12345 is a potential filter as well as ?foo=bar
 */
export const usePotentialFilters = () => {
  const filtersToSubmit = useRef<Array<FilterObjForBulkTracking>>([]);

  const history = useHistory();
  const queryParams = useQueryParams();
  const getQueryParams = useQueryParamsCallback();
  const search = queryParams.toString();

  const filterTracker = useTrackFilter();
  const getCurrentFilters = useCallback(
    () => getFiltersFromQueryParams(getQueryParams()),
    [getQueryParams],
  );
  const filters = getFiltersFromQueryParams(queryParams);

  // read at call time, not render time: memoized setters would otherwise close
  // over a stale snapshot and wipe state keys added in between (e.g. an open
  // flyout's history-backed open flag, ASFE-8284)
  const getHistoryLocationState = useCallback(
    () => ({
      ...((history.location?.state as LocationType['state']) ?? {}),
      isFiltering: true,
    }),
    [history],
  );

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const submitFiltersInBulk = useCallback(() => {
    filtersToSubmit.current.forEach(filter => {
      filterTracker(
        filter.section,
        filter.slug,
        filter.type,
        filter.action,
        filter.label,
      );
    });
    filtersToSubmit.current = [];
  }, [filterTracker]);

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const addFilterForBulkTracking = useCallback(
    ({section, slug, type, action, label}: FilterObjForBulkTracking) => {
      const existingObjIndex = filtersToSubmit.current.findIndex(
        filterObj => filterObj.section === section,
      );
      if (existingObjIndex > -1) {
        filtersToSubmit.current.splice(existingObjIndex, 1);
      }

      filtersToSubmit.current.push({
        section,
        slug,
        type,
        action,
        label,
      });
    },
    [filtersToSubmit],
  );

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const removeFilter = (slug: string, values: string) => {
    const filterValues = values.split(',');
    const slugValues = filters.get(slug);
    const filteredSlugValues = slugValues?.length
      ? slugValues.filter(slugValue => !filterValues.includes(slugValue))
      : undefined;
    history.replace({
      state: getHistoryLocationState(),
      search: getUpdatedFilterParams({
        search,
        update: new Map([[slug, filteredSlugValues]]),
      }),
    });
  };

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const resetAllFilters = () => {
    const update = new Map();

    Array.from(filters.keys()).forEach(filterKey => {
      update.set(filterKey, undefined);
    });

    history.replace({
      state: getHistoryLocationState(),
      search: getUpdatedFilterParams({
        search,
        update,
      }),
    });
  };

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const resetFilter = (slug: string) => {
    history.replace({
      state: getHistoryLocationState(),
      search: getUpdatedFilterParams({
        search,
        update: new Map([[slug, undefined]]),
      }),
    });
  };

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const resetFilters = (slugs: string[]) => {
    const updates = slugs.map(slug => [slug, undefined] as const);
    history.replace({
      state: getHistoryLocationState(),
      search: getUpdatedFilterParams({
        search,
        update: new Map([...updates]),
      }),
    });
  };

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const getFilterToUpdate = useCallback(
    // eslint-disable-next-line complexity -- this rule is disabled since we need to handle all the filters
    ({
      filterId,
      filterSection,
      filterSlug,
      singleSelectOnly,
      label,
      shouldTrackImmediately = true,
      trackingParams,
    }: SetFilterObject) => {
      if (isBooleanFilter(filterId)) {
        const filterObj: FilterObjForBulkTracking = {
          section: filterSection,
          slug: stringToBool(filterId),
          type: 'boolean',
          action: 'submit',
          label,
        };
        if (shouldTrackImmediately && filterId !== '') {
          filterTracker(
            trackingParams?.attributeGroup ?? filterObj.section,
            filterObj.slug,
            filterObj.type,
            filterObj.action,
            filterObj.label,
          );
        } else {
          addFilterForBulkTracking(filterObj);
        }

        return new Map([
          [filterSection, filterId === 'true' ? [filterId] : undefined],
        ]);
      }

      if (singleSelectOnly) {
        const filterValue = getCurrentFilters().get(filterSection);
        const filterValueIsActive = filterValue?.[0] === filterId;

        if (filterValueIsActive) {
          const filterObj: FilterObjForBulkTracking = {
            section: filterSection,
            slug: [''],
            type: 'attribute',
            action: 'submit',
            label,
          };
          if (shouldTrackImmediately) {
            filterTracker(
              filterObj.section,
              filterObj.slug,
              filterObj.type,
              filterObj.action,
              filterObj.label,
            );
          } else {
            addFilterForBulkTracking(filterObj);
          }

          return new Map([[filterSection, undefined]]);
        } else {
          const filterObj: FilterObjForBulkTracking = {
            section: filterSection,
            slug: [filterId],
            type: 'attribute',
            action: 'submit',
            label,
          };
          if (shouldTrackImmediately && filterId !== '') {
            filterTracker(
              filterObj.section,
              filterObj.slug,
              filterObj.type,
              filterObj.action,
              filterObj.label,
            );
          } else {
            addFilterForBulkTracking(filterObj);
          }

          return new Map([[filterSection, [filterId]]]);
        }
      }

      if (isRangeFilter(filterSection)) {
        const range = filterId.split(RANGE_FILTER_DELIMITER);
        const filterObj: FilterObjForBulkTracking = {
          section: filterSection,
          slug: range,
          type: 'range',
          action: 'submit',
          label,
        };
        if (shouldTrackImmediately && filterId !== '') {
          filterTracker(
            filterObj.section,
            filterObj.slug,
            filterObj.type,
            filterObj.action,
            filterObj.label,
          );
        } else {
          addFilterForBulkTracking(filterObj);
        }

        if (range[0] && range[1]) {
          return new Map([[filterSection, range]]);
        }
      }

      const currentValuesString = getQueryParams().get(filterSection);
      const currentSectionFilters: Array<string> = currentValuesString
        ? currentValuesString.split(',')
        : [];

      const selectedFilterAlreadyActive = currentSectionFilters
        ?.map(value => String(parseAttributeFilterValue(value)))
        .includes(filterId);

      let updatedActiveSectionFilters: Array<string> = [];

      if (filterId === '') {
        updatedActiveSectionFilters = currentSectionFilters;
      } else {
        updatedActiveSectionFilters = selectedFilterAlreadyActive
          ? currentSectionFilters.filter(
              filter => String(parseAttributeFilterValue(filter)) !== filterId,
            )
          : [
              ...currentSectionFilters,
              composeAttributeFilterId(filterId, filterSlug),
            ];
      }

      const update = new Map<string, Array<string>>([
        [
          filterSection,
          updatedActiveSectionFilters.length > 0
            ? updatedActiveSectionFilters
            : [],
        ],
      ]);

      const filterObj: FilterObjForBulkTracking = {
        section: filterSection,
        slug: update
          .get(filterSection)!
          .map(value => String(parseAttributeFilterValue(value))),
        type: 'attribute',
        action: 'submit',
        label,
      };
      if (shouldTrackImmediately) {
        filterTracker(
          filterObj.section,
          filterObj.slug,
          filterObj.type,
          filterObj.action,
          filterObj.label,
        );
      } else {
        addFilterForBulkTracking(filterObj);
      }

      return update;
    },
    [
      addFilterForBulkTracking,
      filterTracker,
      getCurrentFilters,
      getQueryParams,
    ],
  );

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const setFilter = useCallback(
    (filter: SetFilterObject) => {
      const filtersToUpdate = getFilterToUpdate(filter);
      history.replace({
        state: getHistoryLocationState(),
        search: getUpdatedFilterParams({
          search: history.location.search,
          update: filtersToUpdate || new Map(),
        }),
      });
    },
    [getFilterToUpdate, getHistoryLocationState, history],
  );

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const setFilters = useCallback(
    (filters: SetFilterObject[]) => {
      const filtersToUpdate = filters.reduce(
        (acc: Map<string, Array<string>>, filter) => {
          const filtersToUpdate = getFilterToUpdate(filter);

          if (filtersToUpdate) {
            return acc.set(
              filter.filterSection,
              filtersToUpdate.get(filter.filterSection) || [],
            );
          }

          return acc;
        },
        new Map(),
      );

      history.replace({
        state: getHistoryLocationState(),
        search: getUpdatedFilterParams({
          search: history.location.search,
          update: filtersToUpdate,
        }),
      });
    },
    [history, getFilterToUpdate, getHistoryLocationState],
  );

  // Set given filter the values passed on the function, unlike other functions, that check if a values is applied or not to toggle it.
  // eslint-disable-next-line local-rules/prefer-use-query-params-callback -- this rule is disabled since we need to use query params outside of the callback anyway.
  const setFiltersWithoutToggle = useCallback(
    (params: SetFiltersWithoutToggleType) => {
      const filtersToUpdate = new Map();

      params.forEach(param => {
        filtersToUpdate.set(param.filterSection, param.filterValues);
      });

      history.replace({
        state: getHistoryLocationState(),
        search: getUpdatedFilterParams({
          search,
          update: filtersToUpdate,
        }),
      });
    },
    [history, search, getHistoryLocationState],
  );

  return {
    filters,
    removeFilter,
    resetAllFilters,
    resetFilter,
    resetFilters,
    setFilter,
    setFilters,
    submitFiltersInBulk,
    addFilterForBulkTracking,
    setFiltersWithoutToggle,
  };
};

const stringToBool = (value: string) => value === 'true';
